import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import AllowedApiClients from 'Services/common/Features/AllowedApiClient/Components/AllowedApiClients'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import {
  useCreateSingleCatalogMutation,
  useUpdateSingleCatalogMutation
} from 'Services/ProductManagement/Features/Catalog/catalogApi'
import { SELECTED_CATALOG_PARAMETER_KEY } from 'Services/ProductManagement/Features/Catalog/Hooks/useCatalogsWithParams'
import type { Catalog } from 'Services/ProductManagement/Features/Catalog/types'
import {
  getPtaQueryParamOrSkip,
  useGetSalesChannelParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import useSelectedTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSelectedTariff'
import { TariffCatalogTabType } from 'Services/ProductManagement/Features/Tariff/types'
import useTypedSelector from 'Store'
import type { ProductTariffError } from 'Util/ApiClient'
import {
  ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH,
  DRAWER_TAB_PARAM_KEY,
  FORM_FIELD_DEFAULT_MIN_WIDTH,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  Service
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: Record<string, string[]> = {
  name: [],
  description: [],
  sales_channel: [],
  catalog_code: []
}

interface TariffCatalogFormProps {
  catalog?: Catalog
  successBaseUrl: string
}

export function TariffCatalogForm({ catalog, successBaseUrl }: Readonly<TariffCatalogFormProps>) {
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const { user } = useTypedSelector(state => state.user)

  const isEdit = !!catalog

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId)

  const [createSingleCatalog, createStatus] = useCreateSingleCatalogMutation()
  const [updateSingleCatalog, updateStatus] = useUpdateSingleCatalogMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)
  const [catalogDetails, setCatalogDetails] = useState<Partial<Catalog>>((isEdit && catalog) || {})

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    catalogDetails?.api_client_read || []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    catalogDetails?.api_client_write || []
  )

  const {
    data: salesChannelParameterGroups = null,
    isFetching: isSalesChannelParameterGroupsFetching
  } = useGetSalesChannelParameterGroupQuery(getPtaQueryParamOrSkip(singleTariff?.pta), {
    refetchOnMountOrArgChange: true
  })

  const salesChannelOptions =
    salesChannelParameterGroups?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []
  // Add empty option for sales channel
  salesChannelOptions.unshift({
    label: '-',
    value: ''
  })

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Catalog
  ) => {
    if (value !== undefined) {
      setCatalogDetails(prevState => ({ ...prevState, [field]: value }) as Catalog)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!singleTariff) {
      SnackbarUtils.error(t`There was an error saving the sales channel.`)
      return
    }

    const catalogData = {
      ...catalogDetails,
      api_client_read: selectedReadClients,
      api_client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleCatalog({ ...catalogData, id: catalog.id })
      : createSingleCatalog({
          ...catalogData,
          tariff_id: singleTariff.id,
          catalog_owner: singleTariff.pta
        })
    mutation
      .unwrap()
      .then((catalogResponse: Catalog) => {
        SnackbarUtils.success(t`Sales channel successfully saved.`)
        if (IS_WALTTI_SERVICE_ENVIRONMENT) {
          navigate(
            appendQueryParamsToUrl(`${successBaseUrl}/${catalogResponse.id}/products/`, {
              [SELECTED_CATALOG_PARAMETER_KEY]: catalogResponse.id,
              [DRAWER_TAB_PARAM_KEY]: TariffCatalogTabType.DETAILS
            })
          )
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_CATALOG_PARAMETER_KEY]: catalogResponse.id,
              [DRAWER_TAB_PARAM_KEY]: TariffCatalogTabType.DETAILS
            })
          )
        }
      })

      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the sales channel.`)
      })
  }

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <WithLoader loading={isSalesChannelParameterGroupsFetching || isSingleTariffLoading}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box>
                <FormTextField
                  name="name"
                  required
                  value={catalogDetails?.name ?? ''}
                  label={t`Name`}
                  maxLength={40}
                  onChange={e => handleChange(e.target.value, 'name')}
                  {...getErrorProps('name')}
                />
              </Box>
              <Box>
                <FormTextField
                  name="catalog_code"
                  required
                  value={catalogDetails?.catalog_code ?? ''}
                  label={t`Catalog code`}
                  onChange={e => handleChange(e.target.value, 'catalog_code')}
                  {...getErrorProps('catalog_code')}
                />
              </Box>
            </Box>
            <Box>
              <FormTextField
                name="description"
                value={catalogDetails?.description ?? ''}
                label={t`Description`}
                multiline
                onChange={e => handleChange(e.target.value, 'description')}
                {...getErrorProps('description')}
              />
            </Box>
            <Box>
              <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }}>
                <InputLabel size="small">{t`Parameter group`}</InputLabel>
                <Select
                  name="parameter_group"
                  size="small"
                  label={t`Parameter group`}
                  value={catalogDetails?.sales_channel ?? []}
                  error={fieldErrors.sales_channel?.length > 0}
                  onChange={e => handleChange(e.target.value, 'sales_channel')}
                >
                  {salesChannelOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.sales_channel?.length > 0}>
                  {getFieldErrorHelperText('sales_channel')}
                </FormHelperText>
              </FormControl>
            </Box>
            {isSuperuser(user) && (
              <Box sx={{ maxWidth: ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH, py: 2 }}>
                <AllowedApiClients
                  service={Service.PRODUCT_MANAGEMENT}
                  selectedReadClients={selectedReadClients}
                  selectedWriteClients={selectedWriteClients}
                  setSelectedReadClients={setSelectedReadClients}
                  setSelectedWriteClients={setSelectedWriteClients}
                />
              </Box>
            )}
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save sales channel`}
            </CustomLoadingButton>
          </StyledForm>
        </WithLoader>
      </Grid>
    </Grid>
  )
}
